/* ────────────────────────────────────────────────────────────────
   src/components/Testimonials.jsx
────────────────────────────────────────────────────────────────── */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const ACCENT = "#E06A00";
const INTERVAL = 6000;

/* ── Testimonios reales ── */
const TESTIMONIALS = [
  {
    quote:
      "¡Excelente inversión! Bajé mi recibo de CFE de $4,500 a solo $180. Todo el proceso fue claro y rápido. KW Solar me explicó todo con paciencia. 100 % recomendado.",
    author: "Alejandro M., Col. Pitic",
  },
  {
    quote:
      "Desde el primer contacto hasta la instalación fueron súper profesionales. Me entregaron mi propuesta en un día y en menos de una semana ya tenía los paneles funcionando.",
    author: "Daniela R., Villa de Parras",
  },
  {
    quote:
      "Lo mejor fue que no tuve que hacer nada de trámites, ellos se encargaron de todo ante la CFE. Muy buen servicio y atención personalizada.",
    author: "Iván C., Puerta de Hierro",
  },
  {
    quote:
      "Yo dudaba por el tema del financiamiento, pero me ofrecieron un plan a mi medida y ahora pago menos al mes en el banco que lo que pagaba de luz.",
    author: "Lorena V., La Joya Residencial",
  },
  {
    quote:
      "Mi casa ahora produce más energía de la que consumo. Recibo saldo a favor con CFE. Los recomiendo a ojos cerrados.",
    author: "Carlos J., La Cima Residencial",
  },
  {
    quote:
      "Busqué varias opciones, pero KW Solar fue el único que me explicó bien todo y me dio garantía escrita. Muy formales y serios.",
    author: "Patricia G., Montecarlo Residencial",
  },
  {
    quote:
      "Me encantó que todo fue transparente, desde el primer presupuesto hasta la instalación. No hubo cargos escondidos ni letras chiquitas.",
    author: "Raúl S., Privada Xochimilco",
  },
  {
    quote:
      "Desde que instalé los paneles hace un año no he tenido problemas, y el servicio postventa ha sido excelente. Siempre responden mis dudas.",
    author: "Silvia H., Las Quintas",
  },
  {
    quote:
      "Muy buen equipo de trabajo, puntuales y con excelente disposición. Recomiendo KW Solar a todos los que quieran ahorrar en su recibo.",
    author: "Jorge M., Los Lagos",
  },
  {
    quote:
      "Además de que ahora ahorro más del 90 % en luz, siento que estoy haciendo algo bueno por el medio ambiente. Gracias KW Solar por ayudarme a dar este paso.",
    author: "Valeria F., Real del Arco",
  },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  /* progreso + rotación automática */
  useEffect(() => {
    setProgress(0);
    const step = 80;
    const inc = (step / INTERVAL) * 100;

    const id = setInterval(() => {
      setProgress((p) => {
        if (p + inc >= 100) {
          clearInterval(id);
          setIndex((i) => (i + 1) % TESTIMONIALS.length);
          return 100;
        }
        return p + inc;
      });
    }, step);

    return () => clearInterval(id);
  }, [index]);

  const t = TESTIMONIALS[index];

  return (
    <Box component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fff" }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: { xs: "block", md: "flex" },
            alignItems: "flex-start",
            gap: { md: 8 },
          }}
        >
          {/* ── Texto lateral ── */}
          <Box sx={{ flexBasis: { md: "46%" }, mb: { xs: 6, md: 0 } }}>
            <Typography
              variant={downSm ? "h4" : "h3"}
              sx={{ fontWeight: 800, mb: 2, lineHeight: 1.15 }}
            >
              Lo Que Dicen <br />
              <Box component="span" sx={{ color: ACCENT }}>
                Nuestros&nbsp;Clientes
              </Box>
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 380, mb: 6 }}
            >
              Conoce algunas historias de familias sonorenses que ya disfrutan
              de energía solar y facturas mínimas gracias a KW Solar.
            </Typography>

            {/* Barra de progreso */}
            <Box
              sx={{
                height: 6,
                width: "100%",
                maxWidth: 360,
                bgcolor: "#e0e0e0",
                borderRadius: 3,
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progress}%`,
                  bgcolor: ACCENT,
                  transition: "width .08s linear",
                }}
              />
            </Box>
          </Box>

          {/* ── Tarjeta ── */}
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                position: "relative",
                bgcolor: "#fff",
                borderRadius: 3,
                p: { xs: 4, md: 6 },
                boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
                minHeight: 260,
              }}
            >
              {/* franja lateral */}
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  width: 16,
                  height: "100%",
                  bgcolor: ACCENT,
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  fontStyle: "italic",
                  lineHeight: 1.6,
                  pr: 4,
                  mb: 4,
                  position: "relative",
                }}
              >
                <FormatQuoteIcon
                  sx={{
                    position: "absolute",
                    left: -32,
                    top: -10,
                    fontSize: 48,
                    color: ACCENT,
                    opacity: 0.15,
                  }}
                />
                {t.quote}
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t.author}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
